/**
 * Trades Poster — Settings
 * Defaults and identifiers from the Settings specification.
 */

export const SETTINGS_DOCUMENT_STATUS = 'Ready for build';

export const PLATFORM_IDENTITY_DEFAULTS = {
  platformDisplayName: 'Trades Poster',
  legalEntityName: 'Trades Poster Pty Ltd',
  abn: '', // Not configured — Platform Owner setup value
};

export const DOMAINS_DEFAULTS = {
  publicWebsiteUrl: 'https://tradesposter.com',
  protectedAdminUrl: '', // Not configured — Platform Owner setup value
  notificationSendingDomain: 'notifications.tradesposter.com',
};

export const CONTACT_EMAIL_DEFAULTS = {
  supportEmail: 'support@tradesposter.com',
  privacyEmail: 'privacy@tradesposter.com',
  securityEmail: 'security@tradesposter.com',
  billingEmail: 'billing@tradesposter.com',
  notificationFromName: 'Trades Poster',
  notificationFromEmail: 'no-reply@notifications.tradesposter.com',
  notificationReplyToEmail: 'support@tradesposter.com',
};

export const REGIONAL_DEFAULTS = {
  country: 'Australia',
  timezone: 'Australia/Melbourne',
  currency: 'AUD',
  defaultLanguage: 'English (Australia)',
  dateFormat: 'DD/MM/YYYY',
  timeFormat: '24-hour',
};

/** Readable label → IANA identifier */
export const TIMEZONE_OPTIONS = [
  { value: 'Australia/Adelaide', label: 'Adelaide (Australia/Adelaide)' },
  { value: 'Australia/Brisbane', label: 'Brisbane (Australia/Brisbane)' },
  { value: 'Australia/Broken_Hill', label: 'Broken Hill (Australia/Broken_Hill)' },
  { value: 'Australia/Darwin', label: 'Darwin (Australia/Darwin)' },
  { value: 'Australia/Eucla', label: 'Eucla (Australia/Eucla)' },
  { value: 'Australia/Hobart', label: 'Hobart (Australia/Hobart)' },
  { value: 'Australia/Lindeman', label: 'Lindeman (Australia/Lindeman)' },
  { value: 'Australia/Lord_Howe', label: 'Lord Howe (Australia/Lord_Howe)' },
  { value: 'Australia/Melbourne', label: 'Melbourne (Australia/Melbourne)' },
  { value: 'Australia/Perth', label: 'Perth (Australia/Perth)' },
  { value: 'Australia/Sydney', label: 'Sydney (Australia/Sydney)' },
];

export const DATE_FORMAT_OPTIONS = [
  { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY (23/09/2026)' },
  { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD (2026-09-23)' },
  { value: 'D MMM YYYY', label: 'D MMM YYYY (23 Sep 2026)' },
];

export const TIME_FORMAT_OPTIONS = [
  { value: '24-hour', label: '24-hour (17:20)' },
  { value: '12-hour', label: '12-hour (5:20 pm)' },
];

export const BRAND_ASSET_DEFAULTS = {
  primaryLogo: '',
  compactBrandIcon: '',
  favicon: '',
};

export const MAINTENANCE_DEFAULTS = {
  status: 'Off',
  startTime: '',
  endTime: '',
  publicWebsite: false,
  customerDashboard: false,
  businessDashboard: false,
  adminPanel: false,
  maintenanceMessage: '',
  internalReason: '',
  platformOwnerMaintenanceAccess: true,
};

export const SIGNUP_DEFAULTS = {
  customerStatus: 'Open',
  customerPublicNotice: '',
  customerInternalReason: '',
  businessStatus: 'Open',
  businessPublicNotice: '',
  businessInternalReason: '',
};

export const ANNOUNCEMENT_DEFAULTS = {
  status: 'Off',
  message: '',
  publicWebsite: false,
  customerDashboard: false,
  businessDashboard: false,
  linkLabel: '',
  linkUrl: '',
  startTime: '',
  endTime: '',
  allowDismissal: true,
};

export const AUTH_REQUIREMENTS = {
  platformOwnerMfa: 'Required and locked',
  coOwnerMfa: 'Required and locked',
  teamMemberMfa: 'Required and locked',
  customerVerifiedMobile: 'Required',
  businessVerifiedMobile: 'Required',
  businessSensitiveActionMfa: 'Required for approved billing, security, credential and publishing actions',
  customerSensitiveReVerification: 'Enabled where required by the approved flow',
  routineSmsChallenge: 'Not used',
  forceLogoutAfterPasswordReset: 'On — locked',
};

export const AUTH_PROVIDER_INFO = {
  authenticationProvider: 'Auth0',
  providerStatus: 'Warning',
  adminAccessGate: 'Approved infrastructure integration',
  lastProviderCheck: 'Unavailable',
  note: 'Existing tenant, applications and Actions require verification',
};

export const SESSION_DEFAULTS = {
  invalidateSessionsAfterPasswordChange: true,
  invalidateSessionsAfterPasswordReset: true,
  publicAdminSessionSeparation: 'Enforced',
  adminHostOnlyCookies: 'Enforced',
};

/** Read-only display of Validation / Limits references — Unavailable until retrieved. */
export const SESSION_RULE_REFERENCES = [
  { label: 'Admin Idle Timeout', value: 'Unavailable — retrieve from Configuration — Limits & Thresholds' },
  { label: 'Customer Idle Timeout', value: 'Unavailable — retrieve from Configuration — Limits & Thresholds' },
  { label: 'Business Idle Timeout', value: 'Unavailable — retrieve from Configuration — Limits & Thresholds' },
  { label: 'Absolute Session Lifetime', value: 'Unavailable — retrieve from Configuration — Limits & Thresholds' },
];

export const PASSWORD_POLICY_DISPLAY = {
  minimumPasswordLength: 'Unavailable — retrieve from Configuration — Validation',
  requiredCharacterTypes: 'Uppercase · Lowercase · Number · Symbol (subject to provider support)',
  passwordResetLinkExpiry: 'Unavailable — retrieve from Configuration — Limits & Thresholds',
  teamTemporaryPasswordExpiry: '24 hours (confirmed unused Team temporary password)',
  failedSignInProtection: 'Unavailable — authentication provider configuration not verified',
  passwordPolicyProviderSupport: 'Retrieved from Integrations',
};

export const NOTIFICATION_METHOD_DEFAULTS = {
  dashboard: 'On',
  email: 'On',
  browserPush: 'On',
};

export const DATA_REQUEST_FORM_IDS = {
  accountExport: 'FORM-000014',
  accountAnonymisation: 'FORM-000015',
  restore: 'FORM-000016',
};

export const EXPORT_SCOPE_OPTIONS = [
  { value: 'Account & Profile', label: 'Account & Profile' },
  { value: 'Connected Records', label: 'Connected Records' },
  { value: 'All Available Data', label: 'All Available Data' },
];

export const EXPORT_FORMAT_OPTIONS = [
  { value: 'CSV (ZIP)', label: 'CSV (ZIP)' },
  { value: 'JSON (ZIP)', label: 'JSON (ZIP)' },
];
